import Vuex, { MutationTree, GetterTree, ActionTree, Module, ActionContext } from 'vuex'
import Vue from 'vue'
import { RootState } from '..'
import campaignRest from '@/rest/campaign'
import trackRest from '@/rest/track'
import accountRest from '@/rest/account'
import setlistRest from '@/rest/setlist'
import spotifyApi from '@/rest/spotify'
import { ArtistPlaylistOwner, ConnectedArtist, FilterAlbumType, FilterInstrumental, FollowItem, RestSetlisTrack, RestSetlistGenerateResult, SetlistTrack, RestTrack, SetlistTrackType, ExternalTrack, TracksReloadJobState } from '@/types'
import competitionRest from '@/rest/competition'
import cms from '@/cms'
import { AxiosError } from 'axios'
import { sleep } from '@/utils/sleep'

Vue.use(Vuex)

// Poll the no-login setlist generation job until it completes or fails.
const SETLIST_JOB_POLL_INTERVAL_MS = 5000
const SETLIST_JOB_TIMEOUT_MS = 3 * 60 * 1000

async function pollSetlistJob (jobId: string): Promise<RestSetlistGenerateResult> {
  const start = Date.now()
  while (Date.now() - start < SETLIST_JOB_TIMEOUT_MS) {
    const status = await setlistRest.getSetlistJob(jobId)
    if (status.state === 'completed') {
      if (status.setlist) {
        return status.setlist
      }
      throw new Error('Playlist generation finished without a result')
    }
    if (status.state === 'failed') {
      throw new Error(status.error || 'Playlist generation failed')
    }
    if (status.state === 'not_found') {
      throw new Error('Playlist generation job not found')
    }
    await sleep(SETLIST_JOB_POLL_INTERVAL_MS)
  }
  throw new Error('Playlist generation timed out')
}

export interface CampaignState {
  id: number
  isLoadingGet: boolean
  isLoadingCreate: boolean
  isLoadingUpdate: boolean
  isLoadingTracksGet: boolean
  isLoadingTracksReload: boolean
  isLoadingSetlistCreate: boolean
  createdAt: Date | null
  playlistName: string
  trackNormalLimit: number|null
  communityPlaylistTrackLimit: number | null
  trackNormalMin: number
  playlistIds: string[]
  artistIds: string[]
  externalTracks: ExternalTrack[]
  tracks: RestTrack[]
  albumTypes: FilterAlbumType[]
  isFirstArtistOnly: boolean
  instrumental: FilterInstrumental
  setlistTrackTypes: SetlistTrackType[]
  setlistImageUrl: string
  communitySetlistImageUrl: string | null
  followItems: FollowItem[]
  connectedArtists: ConnectedArtist[]
  connectedArtistPlaylist: string
  connectedArtistPlaylistOwner: ArtistPlaylistOwner | null
  isCompetition: boolean
  trackIdsSelected: number[]
  setlistUserSetlistId: number|null
  setlistUserPlaylistId: string
  setListUserPlaylistIsAuthenticated: boolean
  setlistPlaylistId: string
  setlistTracks: SetlistTrack[]
  lastTrackReload: TracksReloadJobState | null
}

export const campaignState: CampaignState = {
  id: -1,
  isLoadingGet: true,
  isLoadingCreate: false,
  isLoadingUpdate: false,
  isLoadingTracksGet: false,
  isLoadingTracksReload: false,
  isLoadingSetlistCreate: false,
  createdAt: null,
  playlistName: '',
  trackNormalLimit: 20,
  communityPlaylistTrackLimit: null,
  trackNormalMin: 10,
  playlistIds: [],
  artistIds: [],
  externalTracks: [],
  tracks: [],
  albumTypes: [
    FilterAlbumType.ALBUM,
    FilterAlbumType.APPEARS_ON,
    FilterAlbumType.COMPILATION,
    FilterAlbumType.SINGLE,
  ],
  isFirstArtistOnly: true,
  instrumental: FilterInstrumental.INCLUDE,
  setlistTrackTypes: [SetlistTrackType.NORMAL],
  setlistImageUrl: '',
  communitySetlistImageUrl: null,
  followItems: [],
  connectedArtists: [],
  connectedArtistPlaylist: '',
  connectedArtistPlaylistOwner: null,
  isCompetition: false,
  trackIdsSelected: [],
  setlistUserSetlistId: null,
  setlistUserPlaylistId: '',
  setListUserPlaylistIsAuthenticated: false,
  setlistPlaylistId: '',
  setlistTracks: [],
  lastTrackReload: null,
}

const userSetlistStorageKey = (campaignId: number): string => {
  return `community_playlist:${campaignId}:user_setlist_playlist_id` // session id to maintain last user playlist ID
}

export const campaignMutations: MutationTree<CampaignState> = {
  SET_CAMPAIGN_ID (state: CampaignState, id) {
    state.id = id
  },
  SET_CAMPAIGN_IS_LOADING_GET (state: CampaignState, isLoading) {
    state.isLoadingGet = isLoading
  },
  SET_CAMPAIGN_IS_LOADING_CREATE (state: CampaignState, isLoading) {
    state.isLoadingCreate = isLoading
  },
  SET_CAMPAIGN_IS_LOADING_UPDATE (state: CampaignState, isLoading) {
    state.isLoadingUpdate = isLoading
  },
  SET_CAMPAIGN_IS_LOADING_TRACKS_GET (state: CampaignState, isLoading) {
    state.isLoadingTracksGet = isLoading
  },
  SET_CAMPAIGN_IS_LOADING_TRACKS_RELOAD (state: CampaignState, isLoading) {
    state.isLoadingTracksReload = isLoading
  },
  SET_CAMPAIGN_IS_LOADING_SETLIST_CREATE (state: CampaignState, isLoading: boolean) {
    state.isLoadingSetlistCreate = isLoading
  },
  SET_CAMPAIGN_CREATED_AT (state: CampaignState, createdAt) {
    state.createdAt = createdAt
  },
  SET_CAMPAIGN_PLAYLIST_NAME (state: CampaignState, playlistName) {
    state.playlistName = playlistName
  },
  SET_CAMPAIGN_TRACK_NORMAL_LIMIT (state: CampaignState, trackNormalLimit) {
    state.trackNormalLimit = trackNormalLimit
  },
  SET_CAMPAIGN_TRACK_NORMAL_MIN (state: CampaignState, trackNormalMin) {
    state.trackNormalMin = trackNormalMin
  },
  SET_CAMPAIGN_COMMUNITY_PLAYLIST_TRACK_LIMIT (state: CampaignState, communityPlaylistTrackLimit: number | null) {
    state.communityPlaylistTrackLimit = communityPlaylistTrackLimit
  },
  SET_CAMPAIGN_PLAYLIST_IDS (state: CampaignState, playlistIds) {
    state.playlistIds = playlistIds
  },
  SET_CAMPAIGN_ARTIST_IDS (state: CampaignState, artistIds) {
    state.artistIds = artistIds
  },
  SET_CAMPAIGN_EXTERNAL_TRACKS (state: CampaignState, externalTracks: ExternalTrack[]) {
    state.externalTracks = externalTracks
  },
  ADD_CAMPAIGN_EXTERNAL_TRACK (state: CampaignState, track: ExternalTrack) {
    state.externalTracks = [...state.externalTracks, track]
  },
  REMOVE_CAMPAIGN_EXTERNAL_TRACK (state: CampaignState, index: number) {
    state.externalTracks = state.externalTracks.filter((_, i) => i !== index)
  },
  SET_CAMPAIGN_TRACKS (state: CampaignState, tracks) {
    state.tracks = tracks
  },
  SET_CAMPAIGN_ALBUM_TYPES (state: CampaignState, albumTypes: FilterAlbumType[]) {
    state.albumTypes = albumTypes
  },
  SET_CAMPAIGN_IS_FIRST_ARTIST_ONLY (state: CampaignState, isFirstArtistOnly: boolean) {
    state.isFirstArtistOnly = isFirstArtistOnly
  },
  SET_CAMPAIGN_INSTRUMENTAL (state: CampaignState, instrumental: FilterInstrumental) {
    state.instrumental = instrumental
  },
  SET_CAMPAIGN_SETLIST_TRACK_TYPES (state: CampaignState, setlistTrackTypes: SetlistTrackType[]) {
    state.setlistTrackTypes = setlistTrackTypes
  },
  SET_CAMPAIGN_SETLIST_IMAGE_URL (state: CampaignState, setlistImageUrl: string) {
    state.setlistImageUrl = setlistImageUrl
  },
  SET_CAMPAIGN_COMMUNITY_SETLIST_IMAGE_URL (state: CampaignState, communitySetlistImageUrl: string | null) {
    state.communitySetlistImageUrl = communitySetlistImageUrl
  },
  SET_CAMPAIGN_FOLLOW_ITEMS (state: CampaignState, followItems: FollowItem[]) {
    state.followItems = followItems
  },
  SET_CAMPAIGN_CONNECTED_ARTIST_PLAYLIST (state: CampaignState, connectedArtistPlaylist: string) {
    state.connectedArtistPlaylist = connectedArtistPlaylist
  },
  SET_CAMPAIGN_CONNECTED_ARTIST_PLAYLIST_OWNER (state: CampaignState, owner: ArtistPlaylistOwner | null) {
    state.connectedArtistPlaylistOwner = owner
  },
  SET_CAMPAIGN_CONNECTED_ARTISTS (state: CampaignState, connectedArtists: ConnectedArtist[]) {
    state.connectedArtists = connectedArtists
  },
  SET_CAMPAIGN_IS_COMPETITION (state: CampaignState, isCompetition: boolean) {
    state.isCompetition = isCompetition
  },
  SET_CAMPAIGN_TRACK_IDS_SELECTED (state: CampaignState, trackIdsSelected: number[]) {
    state.trackIdsSelected = trackIdsSelected
  },
  SET_CAMPAIGN_SETLIST_USER_PLAYLIST_ID (state: CampaignState, p: {setlistId: number, playlistId: string, authenticated: boolean, }) {
    state.setlistUserSetlistId = p.setlistId
    state.setlistUserPlaylistId = p.playlistId
    state.setListUserPlaylistIsAuthenticated = p.authenticated
    window.sessionStorage.setItem(userSetlistStorageKey(state.id), JSON.stringify(p))
  },
  SET_CAMPAIGN_SETLIST_PLAYLIST_ID (state: CampaignState, setlistPlaylistId: string) {
    state.setlistPlaylistId = setlistPlaylistId
  },
  SET_CAMPAIGN_SETLIST_TRACKS (state: CampaignState, setlistTracks: SetlistTrack[]) {
    state.setlistTracks = setlistTracks
  },
  SET_CAMPAIGN_LAST_TRACK_RELOAD (state: CampaignState, lastTrackReload: TracksReloadJobState|null) {
    state.lastTrackReload = lastTrackReload
  }

}

export const campaignGetters: GetterTree<CampaignState, RootState> = {
  campaignId: (state: CampaignState) => state.id,
  campaignIsLoadingGet: (state: CampaignState) => state.isLoadingGet,
  campaignIsLoadingCreate: (state: CampaignState) => state.isLoadingCreate,
  campaignIsLoadingUpdate: (state: CampaignState) => state.isLoadingUpdate,
  campaignIsLoadingTracksGet: (state: CampaignState) => state.isLoadingTracksGet,
  campaignIsLoadingTracksReload: (state: CampaignState) => state.isLoadingTracksReload,
  campaignIsLoadingSetlistCreate: (state: CampaignState) => state.isLoadingSetlistCreate,
  campaignEndsAt: (_: CampaignState) => cms.campaign.ends_at,
  campaignIsEnded: (_: CampaignState) => cms.campaign.hasEnded(),
  campaignCreatedAt: (state: CampaignState) => state.createdAt,
  campaignName: (state: CampaignState) => state.playlistName,
  campaignTrackNormalLimit: (state: CampaignState) => state.trackNormalLimit,
  campaignTrackNormalMin: (state: CampaignState) => state.trackNormalMin,
  campaignCommunityPlaylistTrackLimit: (state: CampaignState): number | null => state.communityPlaylistTrackLimit,
  campaignPlaylistIds: (state: CampaignState) => state.playlistIds,
  campaignArtistIds: (state: CampaignState) => state.artistIds,
  campaignTracks: (state: CampaignState) => state.tracks,
  campaignAlbumTypes: (state: CampaignState) => state.albumTypes,
  campaignIsFirstArtistOnly: (state: CampaignState): boolean => state.isFirstArtistOnly,
  campaignInstrumental: (state: CampaignState) => state.instrumental,
  campaignSetlistTrackTypes: (state: CampaignState): SetlistTrackType[] => state.setlistTrackTypes,
  campaignSetlistImageUrl: (state: CampaignState): string => state.setlistImageUrl,
  campaignCommunitySetlistImageUrl: (state: CampaignState): string | null => state.communitySetlistImageUrl,
  campaignIsSetlist!: (state: CampaignState) => state.setlistTrackTypes[0] !== SetlistTrackType.NORMAL,
  campaignFollowItems: (state: CampaignState) => state.followItems,
  campaignConnectedArtists: (state: CampaignState) => state.connectedArtists,
  campaignConnectedArtistPlaylist: (state: CampaignState) => state.connectedArtistPlaylist,
  campaignConnectedArtistPlaylistOwner: (state: CampaignState): ArtistPlaylistOwner | null => state.connectedArtistPlaylistOwner,
  campaignIsCompetition: (state: CampaignState) => state.isCompetition,
  campaignTrackIdsSelected: (state: CampaignState) => state.trackIdsSelected,
  campaignSetlistUserPlaylistId: (state: CampaignState) => state.setlistUserPlaylistId,
  campaignSetlistPlaylistId: (state: CampaignState) => state.setlistPlaylistId,
  campaignSetlistTracks: (state: CampaignState) => state.setlistTracks,
  campaignExternalTracks: (state: CampaignState): ExternalTrack[] => state.externalTracks,
  campaignTracksLastReload: (state: CampaignState): TracksReloadJobState | null => state.lastTrackReload,
}

export const campaignActions: ActionTree<CampaignState, RootState> = {
  async LOAD_CAMPAIGN (ctx: ActionContext<CampaignState, RootState>) {
    ctx.commit('SET_CAMPAIGN_IS_LOADING_GET', true)
    const lastPlaylistJson = window.sessionStorage.getItem(userSetlistStorageKey(ctx.state.id))
    if (lastPlaylistJson) {
      // Find out if we previously generated a no-login playlist
      // If so and we're logged in now that means we just came from
      // spotify login and we want to save the playlist to user's account
      const lastPlaylist = JSON.parse(lastPlaylistJson)
      console.log('Playlist found in session storage', lastPlaylist)
      ctx.commit('SET_CAMPAIGN_SETLIST_USER_PLAYLIST_ID', lastPlaylist)
      if (!lastPlaylist.authenticated && ctx.getters.isAuthenticated) {
        // Move the playlist to user's account
        try {
          await ctx.dispatch('CAMPAIGN_COPY_SETLIST_TO_ACCOUNT')
        } catch (e) {
          // TODO: Swallowing so campaign doesn't fail to load. Do we do anything/show message?
          console.error(e)
        }
      }
    }

    try {
      const data = await campaignRest.get(cms.accessToken, ctx.state.id)

      ctx.commit('SET_CAMPAIGN_PLAYLIST_NAME', data.playlist_name)
      ctx.commit('SET_CAMPAIGN_TRACK_NORMAL_LIMIT', data.setlist_normal_tracks_limit)
      ctx.commit('SET_CAMPAIGN_COMMUNITY_PLAYLIST_TRACK_LIMIT', data.community_playlist_track_limit ?? null)
      ctx.commit('SET_CAMPAIGN_PLAYLIST_IDS', (data as any).playlists.map((a: { spotify_id: string }) => a.spotify_id))
      ctx.commit('SET_CAMPAIGN_ARTIST_IDS', (data as any).artists.map((a: { spotify_id: string }) => a.spotify_id))
      ctx.commit('SET_CAMPAIGN_ALBUM_TYPES', data.filters.artist_album_types)
      ctx.commit('SET_CAMPAIGN_EXTERNAL_TRACKS', data.external_tracks)
      ctx.commit('SET_CAMPAIGN_IS_FIRST_ARTIST_ONLY', data.filters.first_artist_only)
      ctx.commit('SET_CAMPAIGN_INSTRUMENTAL', data.filters.instrumental)
      ctx.commit('SET_CAMPAIGN_SETLIST_TRACK_TYPES', data.setlist_track_types)
      ctx.commit('SET_CAMPAIGN_SETLIST_IMAGE_URL', data.setlist_cover_image)
      ctx.commit('SET_CAMPAIGN_COMMUNITY_SETLIST_IMAGE_URL', data.community_setlist_cover_image)
      ctx.commit('SET_CAMPAIGN_FOLLOW_ITEMS', data.follow)
      ctx.commit('SET_CAMPAIGN_IS_COMPETITION', data.is_competition)
      ctx.commit('SET_CAMPAIGN_CREATED_AT', new Date(data.created_at || ''))

      if (data.artist_playlist) {
        ctx.commit('SET_CAMPAIGN_CONNECTED_ARTIST_PLAYLIST', typeof data.artist_playlist !== 'string' ? data.artist_playlist.spotify_id : '')
        ctx.commit('SET_CAMPAIGN_CONNECTED_ARTIST_PLAYLIST_OWNER', typeof data.artist_playlist !== 'string'
          ? { playlistId: data.artist_playlist.spotify_id, accountId: data.artist_playlist.account_id }
          : null)
      }

      if (Object.keys(data.frontend).length > 0) { // Frontend data may not be available for new campaigns created with defaults
        ctx.commit('SET_CAMPAIGN_TRACK_NORMAL_MIN', data.frontend.campaignTrackNormalMin || 10)

        // in other modules for now
        ctx.commit('SET_PAGE_MODULES_LIST_RESULT', data.frontend.page_modules_result || [])
        ctx.commit('SET_PAGE_MODULES_LIST_END', data.frontend.page_modules_end || [])
        ctx.commit('SET_PAGE_MODULES_LIST_CHART', data.frontend.page_modules_chart || [])

        // theme
        ctx.commit('SET_THEME_IS_DARK', data.frontend.theme.isDark)
        ctx.commit('SET_THEME_PRIMARY_COLOR', data.frontend.theme.primaryColor)
        ctx.commit('SET_THEME_FONT', data.frontend.theme.font)
        ctx.commit('SET_THEME_LOGO_IMAGE_URL', data.frontend.theme.logoImageUrl)
        ctx.commit('SET_THEME_BACKGROUND_IMAGE_URL', data.frontend.theme.backgroundImageUrl)
        ctx.commit('SET_THEME_HEADER_IMAGE_URL', data.frontend.theme.headerImageUrl)
        ctx.commit('SET_THEME_NEWSLETTER_IMAGE_URL', data.frontend.theme.newsletterImageUrl)
        ctx.commit('SET_JOURNEY_STEPS', data.user_journey.steps)
      }
    } catch (e) {
      console.error(e)
      alert(e)
    } finally {
      ctx.commit('SET_CAMPAIGN_IS_LOADING_GET', false)
    }
  },
  async UPDATE_CAMPAIGN (ctx: ActionContext<CampaignState, RootState>) {
    ctx.commit('SET_CAMPAIGN_IS_LOADING_UPDATE', true)

    try {
      const data = await campaignRest.update(cms.accessToken, ctx.state.id, {
        user_journey: {
          // @ts-ignore
          steps: ctx.rootState.journey.steps,
        },
        playlist_name: ctx.state.playlistName,
        artist_playlist: ctx.state.connectedArtistPlaylist,
        artists: ctx.state.artistIds,
        playlists: ctx.state.playlistIds,
        external_tracks: ctx.state.externalTracks,
        filters: {
          instrumental: FilterInstrumental.INCLUDE,
          first_artist_only: ctx.state.isFirstArtistOnly,
          artist_album_types: ctx.state.albumTypes,
        },
        is_competition: ctx.state.isCompetition,
        setlist_normal_tracks_limit: ctx.state.trackNormalLimit,
        community_playlist_track_limit: ctx.state.communityPlaylistTrackLimit || null,
        setlist_track_types: ctx.state.setlistTrackTypes,
        streaming_services: ['spotify'],
        setlist_cover_image: ctx.state.setlistImageUrl || null,
        community_setlist_cover_image: ctx.state.communitySetlistImageUrl || null,
        frontend: {
          page_modules_result: ctx.getters.pageModulesListResult,
          page_modules_end: ctx.getters.pageModulesListEnd,
          page_modules_chart: ctx.getters.pageModulesListChart,
          campaignTrackNormalMin: ctx.getters.campaignTrackNormalMin,
          theme: {
            isDark: ctx.getters.themeIsDark,
            primaryColor: ctx.getters.themePrimaryColor,
            font: ctx.getters.themeFont,
            logoImageUrl: ctx.getters.themeLogoImageUrl,
            headerImageUrl: ctx.getters.themeHeaderImageUrl,
            backgroundImageUrl: ctx.getters.themeBackgroundImageUrl,
            newsletterImageUrl: ctx.getters.themeNewsletterImageUrl,
          },
        },
        follow: ctx.state.followItems,
      })
      ctx.commit('SET_CAMPAIGN_ID', data.id)
      ctx.commit('SET_CAMPAIGN_CREATED_AT', new Date(data.created_at || ''))
      cms.campaignSaved((data.id as number))
    } catch (e) {
      console.log(e)
      alert(e)
    } finally {
      ctx.commit('SET_CAMPAIGN_IS_LOADING_UPDATE', false)
    }
  },
  async LOAD_TRACKS (ctx: ActionContext<CampaignState, RootState>, loadJobState = false) {
    ctx.commit('SET_CAMPAIGN_IS_LOADING_TRACKS_GET', true)

    try {
      const tracks = await trackRest.get(cms.accessToken, ctx.state.id)
      if (loadJobState) {
        const job = await trackRest.getReloadJobState(cms.accessToken, ctx.state.id)
        ctx.commit('SET_CAMPAIGN_LAST_TRACK_RELOAD', job)
      }
      ctx.commit('SET_CAMPAIGN_TRACKS', tracks)
    } catch (e) {
      console.log(e)
      alert(e)
    } finally {
      ctx.commit('SET_CAMPAIGN_IS_LOADING_TRACKS_GET', false)
    }
  },
  async RELOAD_TRACKS (ctx: ActionContext<CampaignState, RootState>) {
    ctx.commit('SET_CAMPAIGN_IS_LOADING_TRACKS_RELOAD', true)

    try {
      await trackRest.reload(cms.accessToken, ctx.state.id)
    } catch (e) {
      console.log(e)
      alert(e)
    } finally {
      ctx.commit('SET_CAMPAIGN_IS_LOADING_TRACKS_RELOAD', false)
    }
  },
  async CAMPAIGN_ACCOUNT_LOAD (ctx: ActionContext<CampaignState, any>) {
    try {
      const data = await accountRest.get(cms.accessToken, ctx.state.id)

      ctx.commit('SET_CAMPAIGN_CONNECTED_ARTISTS', data)
    } catch (e) {
      console.log(e)
      alert(e)
    }
  },
  async CAMPAIGN_ACCOUNT_REVOKE (ctx: ActionContext<CampaignState, any>, id: number) {
    try {
      await accountRest.delete(cms.accessToken, ctx.state.id, id)

      ctx.commit('SET_CAMPAIGN_CONNECTED_ARTISTS', ctx.state.connectedArtists.filter(artist => artist.id !== id))
    } catch (e) {
      console.log(e)
      alert(e)
    }
  },
  async CAMPAIGN_CREATE_SETLIST (ctx: ActionContext<CampaignState, any>) {
    const storageKey = `community_playlist:${ctx.state.id}:user_setlist_playlist_id` // session id to maintain last user playlist ID
    ctx.commit('SET_CAMPAIGN_IS_LOADING_SETLIST_CREATE', true)
    try {
      const tracks: RestSetlisTrack[] = ctx.getters.campaignIsSetlist
        ? [
          {
            id: ctx.state.trackIdsSelected[0],
            track_type: SetlistTrackType.OPENING,
          },
          ...ctx.state.trackIdsSelected
            .filter((_, index) => ![0, ctx.state.trackIdsSelected.length - 1, ctx.state.trackIdsSelected.length - 2].includes(index))
            .map((id) => ({
              id,
              track_type: SetlistTrackType.NORMAL
            })),
          {
            id: ctx.state.trackIdsSelected[ctx.state.trackIdsSelected.length - 2],
            track_type: SetlistTrackType.CLOSING,
          },
          {
            id: ctx.state.trackIdsSelected[ctx.state.trackIdsSelected.length - 1],
            track_type: SetlistTrackType.ENCORE,
          },
        ]
        : ctx.state.trackIdsSelected.map((id) => ({
          id,
          track_type: SetlistTrackType.NORMAL
        }))

      const test = process.env.NODE_MODULES === 'development'
      let playlistId: string
      let setlistId: number

      if (ctx.getters.isAuthenticated) {
        // Logged-in: create the playlist on the user's own account client-side
        // (their own token, no shared-credential exposure), then persist it
        // with the resulting playlist id in a single write.
        const tracksById = new Map<number, RestTrack>(ctx.state.tracks.map((t) => [t.id, t]))
        const spotifyTrackIds = tracks
          .map((t) => tracksById.get(t.id)?.spotify_id)
          .filter((id): id is string => !!id)

        const accessToken = await ctx.dispatch('AUTH_GET_SPOTIFY_ACCESS_TOKEN')

        // Cover image fetched lazily, only now that we're generating. Absence
        // (404) is fine — just create the playlist without one.
        let coverImageBase64: string | undefined
        try {
          coverImageBase64 = (await campaignRest.getSetlistCover(ctx.state.id)).image
        } catch (e) {
          coverImageBase64 = undefined
        }

        const createdPlaylistId = await spotifyApi.createPlaylistWithTracks(accessToken, {
          name: ctx.state.playlistName,
          trackIds: spotifyTrackIds,
          coverImageBase64,
        })

        const res = await setlistRest.create(ctx.getters.authToken, ctx.state.id, {
          test,
          tracks,
          playlist_id: createdPlaylistId,
        })
        playlistId = res.playlist_id
        setlistId = res.id
      } else {
        // No-login: the playlist is created on the shared account server-side,
        // in a background job, so its credentials are never exposed. Enqueue
        // and poll until it completes.
        const { job_id: jobId } = await setlistRest.createNoLogin(ctx.state.id, { test, tracks })
        const setlist = await pollSetlistJob(jobId)
        playlistId = setlist.playlist_id
        setlistId = setlist.id
      }

      ctx.commit('SET_CAMPAIGN_SETLIST_USER_PLAYLIST_ID', {
        playlistId,
        authenticated: ctx.getters.isAuthenticated,
        setlistId,
      })
      // @ts-ignore
      cms.tracking.trackCustomer('Playlist', 'Playlist Created', 'playlist_created_success')
    } catch (e) {
      console.log(e)
      alert(e)
    } finally {
      ctx.commit('SET_CAMPAIGN_IS_LOADING_SETLIST_CREATE', false)
    }
  },
  async CAMPAIGN_COPY_SETLIST_TO_ACCOUNT (ctx: ActionContext<CampaignState, any>) {
    if (!ctx.state.setlistUserSetlistId) {
      return
    }
    if (ctx.state.setListUserPlaylistIsAuthenticated) {
      // Already saved
      return
    }
    const ret = await setlistRest.copyToAccount(ctx.getters.authToken, ctx.state.id, ctx.state.setlistUserSetlistId)
    ctx.commit('SET_CAMPAIGN_SETLIST_USER_PLAYLIST_ID', {
      playlistId: ret.playlist_id,
      authenticated: true,
      setlistId: ctx.state.setlistUserSetlistId,
    })
  },
  async CAMPAIGN_GET_SETLIST (ctx: ActionContext<CampaignState, any>) {
    try {
      const res = await setlistRest.get(ctx.state.id)

      ctx.commit('SET_CAMPAIGN_SETLIST_PLAYLIST_ID', res.playlist_ids.spotify)
      ctx.commit('SET_CAMPAIGN_SETLIST_TRACKS', res.tracks)
    } catch (e) {
      console.log(e)
      alert(e)
    }
  },
  async CAMPAIGN_COMPETITION_SUBSCRIBE (ctx: ActionContext<CampaignState, any>, data: { name: string, email: string, country: string }) {
    try {
      await competitionRest.subscribe(
        ctx.state.id,
        data.email,
        data.name,
        data.country,
      )
    } catch (e) {
      console.log(e)
      if ((e as AxiosError).response?.status === 409) {
        // Ignore duplicate signup
        return
      }
      alert(e)
    }
  },
  async RESET_TRACK_SELECTION (ctx: ActionContext<CampaignState, any>) {
    ctx.commit('SET_CAMPAIGN_TRACK_IDS_SELECTED', [])
  }
}

export const campaignModule: Module<CampaignState, RootState> = {
  state: campaignState,
  mutations: campaignMutations,
  getters: campaignGetters,
  actions: campaignActions,
}
